import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Switch,
  Button,
  IconButton,
  Alert,
  Divider,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TranslateIcon from "@mui/icons-material/Translate";
import LanguageIcon from "@mui/icons-material/Language";
import HelpTooltip from "./HelpTooltip";
import Toast from "./Toast";
import { useUser } from "../context/UserContext";

const MODULE_LANGUAGES = [
  { code: "default", name: "Follow App Language (Default)", native: "Global" },
  { code: "en", name: "English", native: "English" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "hi", name: "Hindi", native: "हिंदी" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
];

/**
 * ModuleSettingsModal Component
 * Provides a module-specific settings modal for every module in the Admin Portal.
 * Configures:
 * - Approval Required: ON/OFF
 * - Module Language: Default (Global) or Specific Language (Telugu, Tamil, etc.)
 * - Live explanation of mobile app visibility and admin alerts
 */
const ModuleSettingsModal = ({
  open,
  onClose,
  moduleKey = "prayer",
  moduleTitle = "Module",
  hideApproval = false,
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const shouldHideApproval =
    hideApproval ||
    moduleKey === "special_prayers" ||
    moduleKey === "special-prayer" ||
    moduleKey === "special-prayer-categories";

  const { user, effectiveOrgId } = useUser();
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [excludeBv, setExcludeBv] = useState(false);
  const [moduleLanguage, setModuleLanguage] = useState("default");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  // Load current module approval and language settings
  useEffect(() => {
    if (!open) return;

    const loadSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const orgId = effectiveOrgId || (user?.org_id !== 1 ? user?.org_id : 1);

        // Load local storage language preference first as immediate baseline
        const savedLang = localStorage.getItem(`gpn_mod_lang_${moduleKey}`) || "default";
        setModuleLanguage(savedLang);

        const res = await fetch(`${API_URL}settings`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ organization_id: orgId }),
        });

        if (res.ok) {
          const data = await res.json();
          const approvals = data.approvalSettings || {};
          const modApproval = approvals[moduleKey];
          const modLangSetting = approvals[`lang_${moduleKey}`]?.approval || modApproval?.language;

          if (modApproval) {
            setApprovalRequired(modApproval.approval === "yes");
            setExcludeBv(modApproval.bv_status === "yes");
          }

          if (modLangSetting && modLangSetting !== "default") {
            setModuleLanguage(modLangSetting);
            localStorage.setItem(`gpn_mod_lang_${moduleKey}`, modLangSetting);
          }
          return;
        }

        // Fallback to local storage
        const saved = localStorage.getItem(`gpn_mod_setting_${moduleKey}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          setApprovalRequired(parsed.approvalRequired ?? false);
          setExcludeBv(parsed.excludeBv ?? false);
          if (parsed.moduleLanguage) {
            setModuleLanguage(parsed.moduleLanguage);
          }
        }
      } catch (e) {
        console.error("Error loading module setting:", e);
        const saved = localStorage.getItem(`gpn_mod_setting_${moduleKey}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          setApprovalRequired(parsed.approvalRequired ?? false);
        }
        const savedLang = localStorage.getItem(`gpn_mod_lang_${moduleKey}`) || "default";
        setModuleLanguage(savedLang);
      }
    };

    loadSettings();
  }, [open, moduleKey, effectiveOrgId, user]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const orgId = effectiveOrgId || (user?.org_id !== 1 ? user?.org_id : 1);

      // Save locally first
      localStorage.setItem(
        `gpn_mod_setting_${moduleKey}`,
        JSON.stringify({
          approvalRequired,
          excludeBv,
          moduleLanguage,
          updatedAt: new Date().toISOString(),
        })
      );

      if (moduleLanguage === "default") {
        localStorage.removeItem(`gpn_mod_lang_${moduleKey}`);
      } else {
        localStorage.setItem(`gpn_mod_lang_${moduleKey}`, moduleLanguage);
      }

      // Dispatch event to notify open components & DomTranslator
      try {
        window.dispatchEvent(
          new CustomEvent("gpn_module_language_changed", {
            detail: { moduleKey, language: moduleLanguage },
          })
        );
      } catch (e) {}

      // Attempt to save to backend API
      const approvalsPayload = {
        [moduleKey]: {
          approval: approvalRequired ? "yes" : "no",
          bv_status: excludeBv ? "yes" : "no",
          language: moduleLanguage,
        },
        [`lang_${moduleKey}`]: {
          approval: moduleLanguage,
          bv_status: "no",
        },
      };

      await fetch(`${API_URL}update-approval-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          approvals: approvalsPayload,
          org_id: orgId,
        }),
      }).catch((err) => console.warn("API update approval fallback to local:", err));

      setToast({
        open: true,
        message: `${moduleTitle} settings and language saved successfully!`,
        severity: "success",
      });

      setTimeout(() => {
        setSaving(false);
        onClose();
      }, 700);
    } catch (e) {
      console.error("Error saving module settings:", e);
      setToast({ open: true, message: "Settings saved locally.", severity: "info" });
      setSaving(false);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isLight ? "#FFFFFF" : "linear-gradient(135deg, #13222f 0%, #1a2f3f 100%)",
            color: isLight ? "#0F172A" : "#FFFFFF",
            borderRadius: 3,
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.35)",
            boxShadow: isLight ? "0 16px 40px rgba(15, 23, 42, 0.12)" : "0 16px 48px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
            pb: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.2}>
            <SettingsIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF", fontSize: 24 }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
              {moduleTitle} Settings
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {!shouldHideApproval && (
            <>
              {/* Main Approval Setting Switch */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  background: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                  border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.1)",
                  mb: 2.5,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                        Approval Required
                      </Typography>
                      <HelpTooltip
                        title="Approval Required"
                        purpose="Configures whether posts or content submitted by users require admin approval before becoming visible."
                        howItWorks="When ON, submitted content stays in 'Pending' status and admins receive an alert. When OFF, verified users post directly."
                        mobileEffect="When ON, mobile app users will NOT see user submissions until approved. When OFF, submissions appear instantly."
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)", mt: 0.3 }}>
                      Require admin review before content is published to the mobile app
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1.2}>
                    <Chip
                      label={approvalRequired ? "ON" : "OFF"}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.75rem",
                        bgcolor: approvalRequired
                          ? (isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.2)")
                          : (isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.1)"),
                        color: approvalRequired
                          ? (isLight ? "#059669" : "#34D399")
                          : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)"),
                        border: isLight
                          ? (approvalRequired ? "1px solid #A7F3D0" : "1px solid #E2E8F0")
                          : `1px solid ${approvalRequired ? "rgba(16, 185, 129, 0.4)" : "rgba(255, 255, 255, 0.2)"}`,
                      }}
                    />
                    <Switch
                      checked={approvalRequired}
                      onChange={(e) => setApprovalRequired(e.target.checked)}
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#10B981",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#10B981",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Paper>

              {/* Dynamic Explanation Cards based on state */}
              {approvalRequired ? (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.12)",
                      border: isLight ? "1px solid #FDE68A" : "1px solid rgba(245, 158, 11, 0.3)",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <HourglassEmptyIcon sx={{ color: isLight ? "#D97706" : "#F59E0B", fontSize: 18 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: isLight ? "#B45309" : "#FDE68A" }}>
                        Approval Mode: Enabled (ON)
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: isLight ? "#78350F" : "rgba(255, 255, 255, 0.85)", fontSize: "0.83rem", lineHeight: 1.5 }}>
                      • User-submitted content will <strong>NOT appear in the mobile app immediately</strong>.<br />
                      • Content will remain in <strong>Pending</strong> status until an admin approves it.<br />
                      • Once approved, it becomes visible in the mobile app for all members.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.8,
                      borderRadius: 2,
                      bgcolor: isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.12)",
                      border: isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                    }}
                  >
                    <NotificationsActiveIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: isLight ? "#0F766E" : "rgba(255, 255, 255, 0.9)", fontSize: "0.83rem" }}>
                      Admins will receive an <strong>alert/notification</strong> whenever new content is waiting for approval.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.12)",
                    border: isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.3)",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <CheckCircleIcon sx={{ color: isLight ? "#059669" : "#10B981", fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: isLight ? "#047857" : "#34D399" }}>
                      Direct Posting Mode (OFF)
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: isLight ? "#065F46" : "rgba(255, 255, 255, 0.85)", fontSize: "0.83rem", lineHeight: 1.5 }}>
                    • <strong>Verified users can post directly</strong> without requiring admin approval.<br />
                    • User submissions will <strong>appear in the mobile app immediately</strong> upon posting.<br />
                    • Content is still monitored and can be edited or removed by admins at any time.
                  </Typography>
                </Box>
              )}

              {/* Module Language Setting Divider */}
              <Divider sx={{ my: 3, borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)" }} />
            </>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              background: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
              border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.1)",
              mb: 1,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <TranslateIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF", fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                    Module Language
                  </Typography>
                  <HelpTooltip
                    title="Module-Specific Language"
                    purpose="Allows setting a dedicated language specifically for this module, overriding the global app language."
                    howItWorks="If set to a specific language (e.g. Telugu), this module will render in that language for both Admin and Mobile users. If set to 'Follow App Language', it uses the global language set in Main Settings."
                    mobileEffect="Mobile users browsing this module will automatically view it in this language."
                  />
                </Box>
                <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)", mt: 0.3 }}>
                  Configure a dedicated language for {moduleTitle} or follow the global setting
                </Typography>
              </Box>

              <Chip
                label={
                  moduleLanguage === "default"
                    ? "Global Inheritance"
                    : `${MODULE_LANGUAGES.find((l) => l.code === moduleLanguage)?.name || moduleLanguage}`
                }
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  bgcolor:
                    moduleLanguage === "default"
                      ? isLight
                        ? "#F1F5F9"
                        : "rgba(255, 255, 255, 0.1)"
                      : isLight
                      ? "#E0F2FE"
                      : "rgba(14, 165, 233, 0.2)",
                  color:
                    moduleLanguage === "default"
                      ? isLight
                        ? "#64748B"
                        : "rgba(255, 255, 255, 0.7)"
                      : isLight
                      ? "#0284C7"
                      : "#38BDF8",
                  border: isLight
                    ? moduleLanguage === "default"
                      ? "1px solid #E2E8F0"
                      : "1px solid #BAE6FD"
                    : "1px solid rgba(255, 255, 255, 0.2)",
                }}
              />
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel id="module-lang-select-label" sx={{ color: isLight ? "#475569" : "#CBD5E1" }}>
                Select Language for {moduleTitle}
              </InputLabel>
              <Select
                labelId="module-lang-select-label"
                value={moduleLanguage}
                label={`Select Language for ${moduleTitle}`}
                onChange={(e) => setModuleLanguage(e.target.value)}
                sx={{
                  bgcolor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isLight ? "#CBD5E1" : "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                {MODULE_LANGUAGES.map((l) => (
                  <MenuItem key={l.code} value={l.code} sx={{ py: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="body2" fontWeight={moduleLanguage === l.code ? 700 : 500}>
                        {l.name}
                      </Typography>
                      {l.code !== "default" && (
                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", ml: 2 }}>
                          {l.native}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                mt: 1.8,
                p: 1.5,
                borderRadius: 2,
                bgcolor:
                  moduleLanguage === "default"
                    ? isLight
                      ? "#F8FAFC"
                      : "rgba(255, 255, 255, 0.03)"
                    : isLight
                    ? "#F0FDFA"
                    : "rgba(13, 148, 136, 0.12)",
                border: isLight
                  ? moduleLanguage === "default"
                    ? "1px dashed #CBD5E1"
                    : "1px solid #CCFBF1"
                  : "1px dashed rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              <LanguageIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF", fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: isLight ? "#334155" : "rgba(255, 255, 255, 0.85)" }}>
                {moduleLanguage === "default"
                  ? "Inherits global app language configured in Main Settings. If admin or user changes app language, this module follows."
                  : `Dedicated mode: Admin portal and Mobile app for ${moduleTitle} will specifically display in ${
                      MODULE_LANGUAGES.find((l) => l.code === moduleLanguage)?.name
                    }.`}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)", px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: isLight ? "#0D9488" : "linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)",
              color: "#FFFFFF",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              borderRadius: "10px",
              boxShadow: isLight ? "0 4px 12px rgba(13, 148, 136, 0.25)" : "0 4px 14px rgba(76, 161, 175, 0.4)",
              "&:hover": {
                background: isLight ? "#0F766E" : "linear-gradient(135deg, #5ab4c3 0%, #34495e 100%)",
              },
            }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast toast={toast} setToast={setToast} />
    </>
  );
};

export default ModuleSettingsModal;
